import {
	createFile,
	createFileFromTemplate,
	DocumentCreationResult
} from "client/Api/Documents/SharePoint/sharePointAction";
import {
	Dialog,
	Form,
	FormattedText,
	FormItem,
	renderReactDialog,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import { NewDocumentType, OfficeDocumentEditor } from "client/Api/Documents/common";
import {
	getItemsInPreferredOrder,
	UserEditorPreference
} from "client_legacy/Documents/officeDocumentsOpenPreference";
import {
	getOpenLocalizationsForNewDocumentType,
	OpenLocalizations
} from "client_legacy/Documents/openLocalizations";
import { publish, SharePointEvent } from "client_legacy/Documents/SharePoint/documentEvents";
import { openFileInWopi } from "client_legacy/Documents/SharePoint/documentOpener";
import {
	calculateForExtension,
	DocumentOpenPossibilities,
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
} from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import {
	DocumentTemplateModel,
	FileViewModel,
	OneNoteNotebookViewModel,
	ListLocationModel
} from "client/Api/Documents/SharePoint/common";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	documentsConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { TextInputWithExtension } from "../../Dialogs/TextInputWithExtension";
import { useEditorPreference } from "../../useEditorPreference";
import * as openInLocalAppAction from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { inline, isNotEmpty } from "client/Validation/rules";
import * as namePolicy from "client_legacy/Documents/SharePoint/documentNamePolicy";

type NewDocumentDialogProps = {
	parentFolder: Folderish;
	onClose: () => void;
} & (
	{
		template?: undefined;
		documentType: NewDocumentType;
	}
	|
	{
		template: DocumentTemplateModel;
		documentType?: undefined;
	}
);

const NewDocumentDialog = (props: NewDocumentDialogProps): ReactElement => {
	const {
		parentFolder,
		template,
		documentType,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>({
		name: ""
	});

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const editorPreference: UserEditorPreference = useEditorPreference();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const { title, extension } = useMemo(
		(): { title: string; extension: string; } => {
			if (documentType !== undefined) {
				switch (documentType) {
					case NewDocumentType.Word:
						return {
							title: localize("_CreateANewWordDocument"),
							extension: ".docx"
						};
					case NewDocumentType.Excel:
						return {
							title: localize("_CreateANewExcelWorkbook"),
							extension: ".xlsx"
						};
					case NewDocumentType.PowerPoint:
						return {
							title: localize("_CreateANewPowerPointPresentation"),
							extension: ".pptx"
						};
					case NewDocumentType.OneNote:
						return {
							title: localize("_CreateANewOneNoteNotebook"),
							extension: ""
						};
					default:
						never(documentType);
				}
			} else {
				return {
					title: localize("_CreateDocumentFrom") + " " + template.name,
					extension: template.resultingExtension
				};
			}
		},
		[
			documentType,
			template?.name,
			template?.resultingExtension
		]
	);

	const documentOpenPossibilities = useMemo(
		(): DocumentOpenPossibilities => calculateForExtension(
			documentType === NewDocumentType.OneNote
				? ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
				: extension
		),
		[documentType, extension]
	);

	const openLocalizations = useMemo(
		(): OpenLocalizations => getOpenLocalizationsForNewDocumentType(
			documentType !== undefined
				? documentType
				: template
					.documentKind
		),
		[documentType, template]
	);

	const { defaultOfficeDocumentEditor } = documentsConfiguration().sharePoint;

	const createHandler = useCallback(
		async (): Promise<TryResult<FileViewModel | OneNoteNotebookViewModel>> => {
			if (isBusy) {
				return {
					wasSuccessful: false
				};
			}

			setIsBusy(true);
			const validation: IRootValidation<FormData> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return {
						wasSuccessful: false
					};
				}

				const { name } = formData;
				const location: ListLocationModel = convertToLocationModel(parentFolder.location);

				let documentCreationResult: DocumentCreationResult;
				if (documentType !== undefined) {
					documentCreationResult = await createFile(
						{
							documentType,
							folderId: parentFolder.id,
							location,
							name: (name + extension).trimEnd()
						},
						globalCancellation
					);
				} else {
					documentCreationResult = await createFileFromTemplate(
						{
							folderId: parentFolder.id,
							templateUrl: template.url,
							location,
							name: (name + extension).trimEnd()
						},
						globalCancellation
					);
				}

				const document: FileViewModel | OneNoteNotebookViewModel = documentCreationResult.newItem;
				publish(SharePointEvent.AddedOrReplaced, document);

				if (documentCreationResult.suffix > 0) {
					alertManager.showSuccess(
						<FormattedText
							text={localize("_ReplacedNameForExistedFileName")}
							thingsToReplace={[
								{ what: "{0}", filler: `(${documentCreationResult.suffix})` }
							]}
						/>
					);
				}

				return {
					wasSuccessful: true,
					result: document
				};
			} catch (e) {
				if (isCancelledError(e)) {
					return {
						wasSuccessful: false
					};
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);

				return {
					wasSuccessful: false
				};
			}
		},
		[
			alertManager,
			documentType,
			extension,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			parentFolder.id,
			parentFolder.location,
			template?.url
		]
	);

	const createAndOpenOnlineHandler = useCallback(
		async (): Promise<void> => {
			const createResult: TryResult<FileViewModel | OneNoteNotebookViewModel> = await createHandler();

			if (!createResult.wasSuccessful) {
				return;
			}

			onClose();

			const createdDocument: FileViewModel | OneNoteNotebookViewModel = createResult.result;

			openFileInWopi(createdDocument);
		},
		[createHandler, onClose]
	);

	const createAndOpenInLocalAppHandler = useCallback(
		async (): Promise<void> => {
			const createResult: TryResult<FileViewModel | OneNoteNotebookViewModel> = await createHandler();

			if (!createResult.wasSuccessful) {
				return;
			}

			onClose();

			const createdDocument: FileViewModel | OneNoteNotebookViewModel = createResult.result;

			try {
				openInLocalAppAction.open(createdDocument);

				if (documentOpenPossibilities.canBeOpenedOnline) {
					showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
						openLocalizations,
						(): void => {
							openFileInWopi(createdDocument);
						}
					);
				}
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_UnableToOpenDocumentType"));
			}
		},
		[
			alertManager,
			createHandler,
			documentOpenPossibilities.canBeOpenedOnline,
			onClose,
			openLocalizations
		]
	);

	const submitHandler = useCallback(
		(): void => {
			const action: () => void = getItemsInPreferredOrder(
				defaultOfficeDocumentEditor,
				[
					[
						OfficeDocumentEditor.Online,
						() => {
							if (!documentOpenPossibilities.canBeOpenedOnline) {
								return null;
							}

							return (): void => {
								createAndOpenOnlineHandler();
							};
						}
					],
					[
						OfficeDocumentEditor.Local,
						() => {
							if (!documentOpenPossibilities.canBeOpenedLocally) {
								return null;
							}

							return (): void => {
								createAndOpenInLocalAppHandler();
							};
						}
					]
				],
				editorPreference
			)[0];

			action();
		},
		[
			createAndOpenInLocalAppHandler,
			createAndOpenOnlineHandler,
			defaultOfficeDocumentEditor,
			documentOpenPossibilities.canBeOpenedLocally,
			documentOpenPossibilities.canBeOpenedOnline,
			editorPreference
		]
	);

	return (
		<Dialog
			trackingId="sharepoint-new-document-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={title}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_DocumentName")} isRequired>
						<TextInputWithExtension
							attachable={formHandler.for("name")}
							fileExtension={extension}
							placeholder={localize("_EnterDocumentName")}
							isMobile={isMobile}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					{getItemsInPreferredOrder<ReactElement | null>(
						defaultOfficeDocumentEditor,
						[
							[
								OfficeDocumentEditor.Online,
								(): ReactElement | null => {
									if (documentOpenPossibilities.canBeOpenedOnline) {
										return (
											<Toolbar.Button
												key="open-online"
												kind="primary"
												onClick={createAndOpenOnlineHandler}
												text={openLocalizations.online.action}
												trackingId="open-online"
											/>
										);
									}

									return null;
								}
							],
							[
								OfficeDocumentEditor.Local,
								(): ReactElement | null => {
									if (documentOpenPossibilities.canBeOpenedLocally) {
										return (
											<Toolbar.Button
												key="open-in-local-app"
												kind="primary"
												onClick={createAndOpenInLocalAppHandler}
												text={openLocalizations.local.action}
												trackingId="open-in-local-app"
											/>
										);
									}

									return null;
								}
							]
						],
						editorPreference
					)}

					<Toolbar.Button
						kind="secondary"
						trackingId="cancel"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
NewDocumentDialog.displayName = "NewDocumentDialog";

type FormData = { name: string; };

const validationConfiguration: IValidationConfiguration<FormData> = createValidationConfiguration();

validationConfiguration.forScalar("name")
	.add(
		isNotEmpty(),
		inline(
			async (value): Promise<InputValidationResult> => {
				const illegalCharacters: string[] = await namePolicy.getIllegalCharacters();

				if (!namePolicy.isValid(value, illegalCharacters)) {
					const illegalCharactersString: string = illegalCharacters.join(" ");

					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_ContainsIllegalCharacters")
							.replace("{0}", illegalCharactersString)
					};
				}

				if (value.indexOf(" ") === 0) {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_NameCantStartWithASpace")
					};
				}

				if (value.trim() === "") {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_FillInFileName")
					};
				}

				return {
					kind: InputValidationResultKind.Valid
				};
			}
		)
	);

export type NewDocumentDialogInitialData = OmitDistributive<NewDocumentDialogProps, "onClose">;

export function useNewDocumentDialog(): [
	element: ReactElement | null,
	open: (props: NewDocumentDialogInitialData) => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<NewDocumentDialogInitialData, void>(
		(props, onClose) => (
			<NewDocumentDialog
				{...props}
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (props: NewDocumentDialogInitialData): Promise<void> => {
			await originalOpen(props);
		},
		[originalOpen]
	);

	return [element, open];
}

export function openNewDocumentDialog(props: NewDocumentDialogInitialData): Promise<void> {
	return new Promise<void>(
		(resolve): void => {
			renderReactDialog(
				(onClose) => (
					<NewDocumentDialog
						{...props}
						onClose={() => {
							onClose();
							resolve();
						}}
					/>
				)
			);
		}
	);
}
